import React, { useCallback, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../../components/ContentBox';
import { FrankoPopLayout, FpButton, EmptyState } from '../../components/frankopop/frankopopShared';
import {
    ListingCard, OfferCard, TradeHistoryCard, CreateListingModal, ConfirmTradeModal,
} from '../../components/frankopop/tradeShared';
import {
    useFrankopopData, fetchWallet, fetchMyTrades,
    cancelListing, acceptOffer, declineOffer, withdrawOffer, SCRAP_UNIT,
} from '../../data/frankopop';

const SECTIONS = [
    { key: 'received', label: 'Offers received' },
    { key: 'listings', label: 'My listings' },
    { key: 'sent', label: 'Offers sent' },
    { key: 'history', label: 'Trade history' },
];

// Names the FrankoPops on one side of a trade, for the confirm dialogs.
function ItemNames({ items }) {
    return <strong>{items.map((p) => `${p.figure?.name} #${p.serialNumber}`).join(', ') || 'nothing'}</strong>;
}

export default function FrankoPopMyTrades({ userData }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const section = SECTIONS.some((s) => s.key === searchParams.get('tab')) ? searchParams.get('tab') : 'received';

    const walletFetch = useCallback(() => (userData ? fetchWallet() : Promise.resolve(null)), [userData]);
    const { data: walletData, refetch: refetchWallet } = useFrankopopData(walletFetch, null);
    const tradesFetch = useCallback(() => (userData ? fetchMyTrades() : Promise.resolve(null)), [userData]);
    const { data, loading, error, refetch } = useFrankopopData(tradesFetch, null);

    const [posting, setPosting] = useState(false);
    const [confirm, setConfirm] = useState(null);

    if (!userData) return <Navigate to="/frankopop/marketplace" replace />;

    const costs = data?.costs || { listing: 25, trade: 10 };
    const scraps = walletData ? walletData.scraps : data?.scraps ?? null;
    const refreshAll = () => { refetch(); refetchWallet(); };

    const run = async (action, successMessage) => {
        const res = await action();
        setConfirm(null);
        if (!res.success) { toast.error(res.message); refreshAll(); return; }
        toast.success(successMessage);
        refreshAll();
    };

    const askCancel = (listing) => setConfirm({
        title: 'Cancel this listing?',
        body: <>Your FrankoPops go back to your collection and any pending offers are closed. The {costs.listing} {SCRAP_UNIT} you paid to post it are not refunded.</>,
        label: 'Cancel listing',
        variant: 'danger',
        onConfirm: () => run(() => cancelListing(listing.id), 'Listing cancelled.'),
    });
    const askAccept = (offer) => setConfirm({
        title: 'Accept this offer?',
        body: (
            <>
                You give <ItemNames items={offer.listing?.items || []} /> and get <ItemNames items={offer.items} /> from{' '}
                {offer.offerer?.habboName || 'them'}. The swap happens straight away, and every other offer on this listing is closed.
            </>
        ),
        label: 'Accept trade',
        onConfirm: () => run(() => acceptOffer(offer.id), 'Trade complete!'),
    });
    const askDecline = (offer) => setConfirm({
        title: 'Decline this offer?',
        body: <>{offer.offerer?.habboName || 'They'} will see it as declined. Your listing stays up.</>,
        label: 'Decline',
        variant: 'danger',
        onConfirm: () => run(() => declineOffer(offer.id), 'Offer declined.'),
    });
    const doWithdraw = (offerId) => run(() => withdrawOffer(offerId), 'Offer withdrawn.');

    const received = data?.offersReceived || [];
    const listings = data?.listings || [];
    const activeListings = listings.filter((l) => l.status === 'active');
    const pastListings = listings.filter((l) => l.status !== 'active');
    const sent = data?.offersSent || [];
    const pendingSent = sent.filter((o) => o.status === 'pending');
    const pastSent = sent.filter((o) => o.status !== 'pending');
    const history = data?.history || [];

    const counts = {
        received: received.length,
        listings: activeListings.length,
        sent: pendingSent.length,
        history: history.length,
    };

    const setSection = (key) => setSearchParams(key === 'received' ? {} : { tab: key });

    const renderSection = () => {
        if (section === 'received') {
            return received.length === 0
                ? <EmptyState title="No offers waiting" sub="When someone makes an offer on one of your listings it shows up here." />
                : <div className="space-y-3">{received.map((o) => <OfferCard key={o.id} offer={o} mode="received" onAccept={askAccept} onDecline={askDecline} />)}</div>;
        }
        if (section === 'listings') {
            return (
                <div className="space-y-5">
                    {activeListings.length === 0 ? (
                        <EmptyState
                            title="No active listings"
                            sub="Put something on the trading board and it shows up here."
                            action={<FpButton variant="primary" onClick={() => setPosting(true)}>Post a trade</FpButton>}
                        />
                    ) : (
                        <div className="space-y-3">
                            {activeListings.map((l) => <ListingCard key={l.id} listing={l} loggedIn onCancel={askCancel} />)}
                        </div>
                    )}
                    {pastListings.length > 0 && (
                        <div>
                            <p className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-600 mb-2">Past listings</p>
                            <div className="space-y-3">
                                {pastListings.map((l) => <ListingCard key={l.id} listing={l} loggedIn showStatus />)}
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        if (section === 'sent') {
            return sent.length === 0 ? (
                <EmptyState title="No offers sent" sub="Make an offer on a listing in the marketplace and track it here." />
            ) : (
                <div className="space-y-3">
                    {[...pendingSent, ...pastSent].map((o) => <OfferCard key={o.id} offer={o} mode="sent" onWithdraw={doWithdraw} />)}
                </div>
            );
        }
        return history.length === 0
            ? <EmptyState title="No trades yet" sub="Completed trades show up here with what you gave and what you got." />
            : <div className="space-y-3">{history.map((t) => <TradeHistoryCard key={t.id} trade={t} />)}</div>;
    };

    return (
        <FrankoPopLayout active="trades" wallet={walletData ? walletData.cards : null} scraps={scraps}>
            <ContentBox theme="default" tint="darkcyan" header="My trades">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex flex-wrap gap-1.5">
                        {SECTIONS.map((s) => (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => setSection(s.key)}
                                className={`font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-3 py-1.5 rounded transition-all ${
                                    section === s.key ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                            >
                                {s.label}
                                {counts[s.key] > 0 && (
                                    <span className={`ml-1.5 px-1.5 text-[12px] ${section === s.key ? 'bg-white/20' : 'bg-white text-maroon'}`}>{counts[s.key]}</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <FpButton variant="primary" onClick={() => setPosting(true)}>Post a trade</FpButton>
                </div>

                {loading && !data ? (
                    <p className="text-sm text-gray-500">Loading your trades...</p>
                ) : error ? (
                    <p className="text-sm text-red-600">Couldn&apos;t load your trades - {error}</p>
                ) : (
                    <div className={loading ? 'opacity-60' : ''}>{renderSection()}</div>
                )}
            </ContentBox>

            {posting && (
                <CreateListingModal
                    scraps={scraps}
                    cost={costs.listing}
                    onClose={() => setPosting(false)}
                    onCreated={() => { setPosting(false); setSection('listings'); refreshAll(); }}
                />
            )}
            {confirm && (
                <ConfirmTradeModal
                    title={confirm.title}
                    confirmLabel={confirm.label}
                    variant={confirm.variant}
                    onConfirm={confirm.onConfirm}
                    onClose={() => setConfirm(null)}
                >
                    {confirm.body}
                </ConfirmTradeModal>
            )}
        </FrankoPopLayout>
    );
}
